/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */

import {
    FC,
    $Sprite,
    $Container,
    ExtendContainerProps,
    getProp,
    $NineSlicePlane,
    $GraphicsBox
} from '@gongxigames/framework/core/fragments/BuilderY';
import {Store} from '@gongxigames/framework/core/fragments/store/Store';
import {RUnit} from '@gongxigames/framework/core/fragments/display/responsive';
import {Container as PixiContainer} from 'pixi.js';
import {RADIUS, getLayouts, getSliderTexture, updateValueOnChange} from './etc';
import {$FullScreenSensor, PointerEvent} from '../FullScreenSensor';

export type FixedValSliderProps = ExtendContainerProps<{
    size: RUnit;
    disabled?: boolean;
    action?: (n: number) => void;
    values: number[];
    value: number;
}>;

export const $FixedValSlider: FC<FixedValSliderProps> = (props) => {
    props.disabled ??= false;

    const {action, value, values, disabled, ...containerProps} = props;

    const hitBoxContainer = Store.signal<PixiContainer | undefined>();

    const pressed = Store.signal(false);
    const hover = Store.signal(false);
    const active = Store.computed(() => !getProp(props.disabled));

    const initIndex = getProp(props.values).indexOf(getProp(props.value));
    const segment = Store.computed(() => 1 / (getProp(props.values).length - 1));
    const dotPos = Store.signal(initIndex * segment.value);
    const index = Store.computed(() => Math.round(dotPos.value / segment.value));
    const snapVal = Store.computed(() => index.value * segment.value);

    const sliderTexture = getSliderTexture(disabled, pressed, hover);

    const {responsiveContainer, responsiveFill, responsivePoint} = getLayouts(props, dotPos, snapVal);

    const setPointerDown = () => pressed.value = true;
    const setPointerUp = () => pressed.value = false;
    const setHoverTrue = () => hover.value = true;
    const setHoverFalse = () => hover.value = false;

    const offUpdateValueOnChange = updateValueOnChange(props, index);

    function onPointer(event: PointerEvent) {
        if (!event.target || !pressed.value) {
            return;
        }

        const bBox = event.target.getBounds();
        const point = event.data.global;
        const x = point.x - bBox.x - RADIUS/2;
        const val = x / (bBox.width - RADIUS);

        dotPos.value = Math.min(Math.max(val, 0.0), 1.0);
    }

    // Do not remove
    <$FullScreenSensor
        onPointerMove={event => {
            onPointer(event);
        }}
        onPointerUp={event => {
            onPointer(event);
            setPointerUp();
        }}
        enable={pressed}
        hitBoxContainer={hitBoxContainer}
    />;

    return <$Container name='FixedSlider' {...containerProps} responsive={responsiveContainer}>
        <$Container
            responsive={{
                box: {width: '100%', height: '100%'},
                padding: ['0', `${RADIUS}px`]
            }}
        >
            <$NineSlicePlane
                texture='slider_line_bg_sys_ui'
                slices={[0, 6]}
                responsive={{
                    updateWidth: true,
                    box: {width: '100%', height: '96px', y: '50%'},
                    translate: {y: '1px'},
                }}
            />
            <$NineSlicePlane
                texture='slider_line_full_sys_ui'
                slices={[0, 6]}
                responsive={responsiveFill}
            />
            <$Sprite
                texture={sliderTexture}
                responsive={responsivePoint}
                anchor={0.5}
            />
        </$Container>
        <$GraphicsBox
            ref={item => hitBoxContainer.value = item}
            responsiveBoxSize
            responsive={{
                box: {width: '100%', height: '100%'}
            }}
            boxColor={0}
            renderable={false}
            interactive={active}
            buttonMode={active}
            events={{
                pointerover: setHoverTrue,
                pointerout: setHoverFalse,
                pointerdown: [setPointerDown, onPointer],
                destroyed: offUpdateValueOnChange
            }}
        />
    </$Container>;
};